import { useCallback, useState } from 'react';

/**
 * Client-side soft cap for guest uploads.
 *
 * IMPORTANT: this is a UX nudge, NOT a security control. It lives in
 * `localStorage` and is trivially bypassable (incognito / clearing storage).
 * Real abuse/cost protection (per-IP rate limiting at the edge/WAF) is a
 * separate, deferred concern — see docs/internal/GUEST_UPLOAD_NEXT_PHASE.md.
 * Its only job is to encourage a guest who has tried the product a few times
 * to create an account.
 */
const STORAGE_KEY = 'guestUploadCount';
export const GUEST_UPLOAD_LIMIT = 3;

function readCount(): number {
  if (typeof window === 'undefined') return 0;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  const parsed = raw ? parseInt(raw, 10) : 0;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

export function useGuestUploadCap() {
  const [count, setCount] = useState<number>(readCount);

  const recordUpload = useCallback(() => {
    setCount((prev) => {
      const next = prev + 1;
      try {
        window.localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        // Private-mode / storage-disabled: degrade gracefully. The worst case
        // is the guest gets a few extra free tries, which is acceptable for a
        // UX-only nudge.
      }
      return next;
    });
  }, []);

  return {
    count,
    remaining: Math.max(0, GUEST_UPLOAD_LIMIT - count),
    limitReached: count >= GUEST_UPLOAD_LIMIT,
    limit: GUEST_UPLOAD_LIMIT,
    recordUpload,
  };
}
